
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ConfigValue struct
 */
class ConfigValue {
  /**
   * Initialize struct
   * @param {!string=} content
   * @param {!number=} version
   * @constructor
   */
  constructor (content = '', version = new UInt64(0, 0)) {
    this.Content = content
    this.Version = version
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigValue}
   * @param {!ConfigValue} other Other struct
   * @returns {!ConfigValue} This struct
   */
  copy (other) {
    if (other.Content != null) {
      this.Content = other.Content
    } else {
      this.Content = null
    }
    if (other.Version != null) {
      this.Version = UInt64.fromNumber(other.Version)
    } else {
      this.Version = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigValue}
   * @returns {!ConfigValue} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigValueModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigValueModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigValue}
   * @param {!ConfigValue} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigValue)) {
      throw new TypeError('Instance of ConfigValue is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigValue}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Content: ((this.Content != null) ? this.Content : null),
      Version: ((this.Version != null) ? this.Version.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigValue.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigValue} other Object value
   * @returns {!ConfigValue} Created struct
   */
  static fromObject (other) {
    return new ConfigValue().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigValue}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigValue.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigValue}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 1006
  }
}

export { ConfigValue };

/**
 * Fast Binary Encoding ConfigValue field model
 */
class FieldModelConfigValue extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Content = new fbe.FieldModelString(buffer, 4 + 4)
    this.Version = new fbe.FieldModelUInt64(buffer, this.Content.fbeOffset + this.Content.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Content.fbeSize + this.Version.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Content.fbeExtra + this.Version.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigValue.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 1006
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigValue}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigValue}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Content.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Content.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Content.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigValue}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigValue}
   * @param {!ConfigValue} fbeValue Default value, defaults is new ConfigValue()
   * @returns {!ConfigValue} ConfigValue value
   */
  get (fbeValue = new ConfigValue()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigValue}
   * @param {!ConfigValue} fbeValue ConfigValue value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Content.fbeSize) <= fbeStructSize) {
      fbeValue.Content = this.Content.get()
    } else {
      fbeValue.Content = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Content.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) <= fbeStructSize) {
      fbeValue.Version = this.Version.get()
    } else {
      fbeValue.Version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigValue}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigValue}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigValue}
   * @param {!ConfigValue} fbeValue ConfigValue value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigValue}
   * @param {ConfigValue} fbeValue ConfigValue value
   */
  setFields (fbeValue) {
    this.Content.set(fbeValue.Content)
    this.Version.set(fbeValue.Version)
  }
}

export { FieldModelConfigValue };

/**
 * Fast Binary Encoding ConfigValue model
 */
class ConfigValueModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigValue(this.buffer, 4)
  }

  /**
   * Get the ConfigValue model
   * @this {!ConfigValueModel}
   * @returns {!FieldModelConfigValue} model ConfigValue model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigValueModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigValueModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigValueModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigValueModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigValue.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigValueModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigValueModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigValueModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigValueModel}
   * @param {!ConfigValue} value ConfigValue value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigValueModel}
   * @param {!ConfigValue} value ConfigValue value, defaults is new ConfigValue()
   * @return {!object} Deserialized ConfigValue value and its size
   */
  deserialize (value = new ConfigValue()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigValue(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigValue(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigValueModel}
   * @param {!number} prev Previous ConfigValue model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigValueModel };
